import React, { useCallback, useEffect, useState } from "react";
import { AuthStatus, useAuthContext, useAuthContextControl } from "../contexts/AuthProvider";
import {
    Household,
    HouseholdUser,
    Integration,
    UserFull,
    deleteIntegration,
    editHousehold,
    getHouseholdUsers,
    getUserIntegrations,
    householdRemoveUser,
    imageIdToUrl,
    inviteToHousehold,
    setUserAvatar,
} from "../data";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, Paper, TextField } from "@mui/material";
import tickTickLogo from "../icons/ticktick-Logo-horizontal-small.png";
import { Helmet } from "react-helmet";
import EditableText from "./base/EditableText";
import EditableAvatar from "./EditableAvatar";
import ConfirmDialog from "./base/ConfirmDialog";

interface IntegrationElementProps {
    integration: Integration;
    onDelete: (integration: Integration) => void;
}

function IntegrationElement({ integration, onDelete }: IntegrationElementProps) {
    let innerContent: React.ReactNode;

    if (integration.expired) {
        innerContent = (
            <>
                <span style={{ color: "red" }}>Inloggningen har gått ut</span>
                <Button variant="outlined" href="/login/tick-tick" LinkComponent="a">
                    Förnya
                </Button>
            </>
        );
    } else {
        innerContent = (
            <Button variant="outlined" onClick={() => onDelete(integration)}>
                <DeleteIcon></DeleteIcon>
            </Button>
        );
    }

    return (
        <Paper key={integration.id} sx={{ padding: "15px" }}>
            <Stack direction="row" alignItems="center" spacing="15px">
                <img src={tickTickLogo} loading="lazy" height="40px"></img>
                {innerContent}
            </Stack>
        </Paper>
    );
}

function HouseholdUserView({
    user,
    removeAllowed,
    onRemove,
}: {
    user: HouseholdUser;
    removeAllowed: boolean;
    onRemove: () => void;
}) {
    const showRemove = removeAllowed && user.relation !== "Owner";

    return (
        <Paper sx={{ padding: "10px" }}>
            <Stack direction="row" alignItems="center" useFlexGap spacing="15px">
                <Avatar
                    alt={user.name}
                    src={imageIdToUrl("thumb", user.avatar_image_id)}
                    sx={{ width: 30, height: 30 }}
                />
                <div>{user.name}</div>
                <div>
                    (<b>{user.relation}</b>)
                </div>
                {showRemove ? <Button onClick={onRemove}>Remove</Button> : <></>}
            </Stack>
        </Paper>
    );
}

interface HouseholdViewProps {
    household: Household;
    users: HouseholdUser[];
    me: UserFull;
    onRemoveUser: (userId: number) => Promise<void>;
}

function HouseholdView({ household, me, users, onRemoveUser }: HouseholdViewProps) {
    const authControl = useAuthContextControl();
    const [isCreatingInvite, setIsCreatingInvite] = useState<boolean>(false);
    const [inviteUrl, setInviteUrl] = useState<string | undefined>();
    const [removeUserOpen, setRemoveUserOpen] = useState<boolean>(false);
    const [removeUser, setRemoveUser] = useState<HouseholdUser | undefined>();

    const onCreateInvite = async () => {
        setIsCreatingInvite(true);
        const url = await inviteToHousehold(household.id);
        setInviteUrl(`${window.location.origin}${url}`);
        setIsCreatingInvite(false);
    };

    const onChangeHouseholdName = useCallback(
        async (newName: string) => {
            await editHousehold(household.id, { name: newName });
            await authControl?.refresh();
        },
        [household, authControl]
    );

    const onUpdateHouseholdImage = useCallback(
        async (image_id: number) => {
            await editHousehold(household.id, { avatar_image_id: image_id });
            await authControl?.refresh();
        },
        [household, authControl]
    );

    const inviteContent = inviteUrl ? (
        <TextField InputProps={{ readOnly: true }} label="Invite url" defaultValue={inviteUrl} fullWidth></TextField>
    ) : (
        <Button variant="contained" onClick={onCreateInvite} disabled={isCreatingInvite}>
            Create Invite
        </Button>
    );

    const amIOwner = users.find(u => u.id === me.id)?.relation === "Owner";

    return (
        <Paper sx={{ padding: "15px" }}>
            <Stack direction="column" spacing="15px">
                <EditableAvatar
                    key={household.avatar_image_id}
                    name={household.name}
                    avatar_image_id={household.avatar_image_id}
                    onCommit={onUpdateHouseholdImage}
                />
                <EditableText
                    value={household.name}
                    onCommit={onChangeHouseholdName}
                    disabled={!amIOwner}
                ></EditableText>
                <Stack direction="column" alignItems="flex-start" spacing="5px">
                    {users.map(u => {
                        const onRemove = () => {
                            setRemoveUser(u);
                            setRemoveUserOpen(true);
                        };
                        return (
                            <HouseholdUserView
                                key={u.id}
                                user={u}
                                removeAllowed={amIOwner}
                                onRemove={onRemove}
                            ></HouseholdUserView>
                        );
                    })}
                    {inviteContent}
                </Stack>
                <ConfirmDialog
                    open={removeUserOpen}
                    title={`Ta bort användare`}
                    confirmText={`Ta bort`}
                    onCancel={() => setRemoveUserOpen(false)}
                    onConfirm={() => {
                        if (!removeUser) return;

                        onRemoveUser(removeUser.id);
                        setRemoveUserOpen(false);
                    }}
                >
                    Vill du ta bort {removeUser?.name} från {household.name}?
                </ConfirmDialog>
            </Stack>
        </Paper>
    );
}

export default function ProfileView() {
    const auth = useAuthContext();
    const authControl = useAuthContextControl();
    const [householdUsers, setHouseholdUsers] = useState<Map<number, HouseholdUser[]>>(new Map());
    const [integrations, setIntegrations] = useState<Integration[]>([]);
    const [removeIntegrationOpen, setRemoveIntegrationOpen] = useState<boolean>(false);
    const [removeIntegration, setRemoveIntegration] = useState<Integration | undefined>();

    const onUpdateUserImage = useCallback(
        async (image_id: number) => {
            await setUserAvatar(image_id);
            await authControl?.refresh();
        },
        [authControl]
    );

    useEffect(() => {
        if (auth.status !== AuthStatus.SignedIn) {
            return;
        }

        const user = auth.user;
        for (const household of user.households) {
            getHouseholdUsers(household.id).then(users => {
                setHouseholdUsers(householdUsers => {
                    householdUsers.set(household.id, users);
                    return new Map(householdUsers);
                });
            });
        }

        getUserIntegrations().then(setIntegrations);
    }, [auth.status]);

    const onDeleteIntegrationCallback = useCallback(
        (integration: Integration) => {
            setRemoveIntegration(integration);
            setRemoveIntegrationOpen(true);
        },
        [setRemoveIntegration, setRemoveIntegrationOpen]
    );

    if (auth.status !== AuthStatus.SignedIn) {
        return <div></div>;
    }

    const onDeleteIntegration = (integration: Integration) => {
        deleteIntegration(integration.id).then(getUserIntegrations).then(setIntegrations);
    };

    const user = auth.user;

    return (
        <div>
            <Helmet>
                <title>Profil</title>
            </Helmet>
            <Stack direction={{ md: "row" }} sx={{ margin: "20px" }} spacing="20px">
                <Stack direction="column" alignItems="center">
                    <EditableAvatar
                        key={user.avatar_image_id}
                        name={user.name}
                        avatar_image_id={user.avatar_image_id}
                        onCommit={onUpdateUserImage}
                    />
                    <h2>{user.name}</h2>
                </Stack>
                <Stack sx={{ flexGrow: 1 }}>
                    <h2>Hushåll</h2>
                    {user.households.map(h => {
                        const users = householdUsers.get(h.id) ?? [];
                        const onRemoveUser = async (userId: number) => {
                            const newUserList = await householdRemoveUser(h.id, userId);
                            setHouseholdUsers(householdUsers => {
                                householdUsers.set(h.id, newUserList);
                                return new Map(householdUsers);
                            });
                        };

                        return (
                            <HouseholdView
                                key={h.id}
                                household={h}
                                me={user}
                                users={users}
                                onRemoveUser={onRemoveUser}
                            ></HouseholdView>
                        );
                    })}
                    <h2>Integrationer</h2>
                    <Stack direction="column" spacing="15px">
                        {integrations.map(integration => (
                            <IntegrationElement
                                key={integration.id}
                                integration={integration}
                                onDelete={onDeleteIntegrationCallback}
                            ></IntegrationElement>
                        ))}
                        <Paper sx={{ padding: "15px" }}>
                            <Stack direction="row" alignItems="center" spacing="15px">
                                <span>Lägg till</span>
                                <Button variant="outlined" href="/login/tick-tick" LinkComponent="a">
                                    <img src={tickTickLogo} loading="lazy" height="40px"></img>
                                </Button>
                            </Stack>
                        </Paper>
                    </Stack>
                </Stack>
            </Stack>
            <ConfirmDialog
                open={removeIntegrationOpen}
                title={`Ta bort integration`}
                confirmText={`Ta bort`}
                onCancel={() => setRemoveIntegrationOpen(false)}
                onConfirm={() => {
                    if (!removeIntegration) return;

                    onDeleteIntegration(removeIntegration);
                    setRemoveIntegrationOpen(false);
                }}
            >
                Vill du ta bort integrationen till TickTick?
            </ConfirmDialog>
        </div>
    );
}
